import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { pluralizeToString } from 'lib/string/pluralize'
import React from 'react'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import SolidCircleIcon from 'components/Luxkit/Icons/solid/SolidCircleIcon'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'

interface Props {
  fromPrice: number;
  duration: number;
  withFlights?: boolean;
  roomCount: number;
  special?: boolean;
  specialName?: string;
  displayMemberPrice: boolean;
  hasLoyaltyRoomUpgrade?: boolean;
}

function OfferCalendarSidebarPriceBlurb(props: Props) {
  const {
    fromPrice,
    duration,
    withFlights,
    roomCount,
    special,
    specialName = 'Limited time special',
    displayMemberPrice,
    hasLoyaltyRoomUpgrade,
  } = props

  const averagePrice = Math.ceil(fromPrice / (roomCount > 1 ? roomCount : duration))
  const unit = roomCount > 1 ? 'room' : 'night'

  const [tier] = useCurrentLoyaltyTier()
  const canLoyaltyUpgrade = useUserHasLoyaltyBenefit('hotel_upgrade')

  return (
    <Group
      direction="vertical"
      gap={4}
      tabletDirection="horizontal"
      tabletHorizontalAlign="space-between"
    >
      {!withFlights && <BodyText variant="small" colour="neutral-one">
        {displayMemberPrice && <BodyText weight="bold" colour="lux-plus" variant="small" as="span">
          Showing {LUXURY_PLUS.PROGRAM_NAME} member prices{' '}
        </BodyText>}
        {roomCount > 1 && `${pluralizeToString('room', roomCount)}, `}
        {pluralizeToString('night', duration)} from{' '}
        <b><FormatCurrency value={fromPrice} /></b> total{' '}<CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span">
          Average <FormatCurrency value={averagePrice} />/{unit}
        </BodyText>
      </BodyText>}
      {withFlights && <BodyText variant="small" colour="neutral-one">
        {displayMemberPrice && <BodyText weight="bold" colour="lux-plus" variant="small" as="span">
          Showing {LUXURY_PLUS.PROGRAM_NAME} member prices{' '}
        </BodyText>}
        {roomCount > 1 && `${pluralizeToString('room', roomCount)}, `}
        {pluralizeToString('night', duration)} + round trip flights from{' '}
        <b><FormatCurrency value={fromPrice} />*</b> (2 adults){' '}<CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span">
          Average <FormatCurrency value={averagePrice} />/{unit}
        </BodyText>
      </BodyText>}
      <Group direction="horizontal" gap={24}>
        {special && <CaptionTextBlock
          variant="medium"
          colour="neutral-two"
          startIcon={<SolidFireIcon colour="urgency" />}
        >
          {specialName}
        </CaptionTextBlock>}
        {hasLoyaltyRoomUpgrade && canLoyaltyUpgrade && <CaptionTextBlock
          variant="medium"
          colour="neutral-two"
          startIcon={<SolidCircleIcon colour={`tier-${tier}`} />}
        >
          Free room upgrade available
        </CaptionTextBlock>}
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((currentState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    duration: state.duration,
    withFlights: state.flights,
    rooms: state.rooms,
    displayMemberPrice: state.displayMemberPrice,
  }
})(OfferCalendarSidebarPriceBlurb)
